import { useQuery } from '@tanstack/react-query';
import {
  getFieldMetadata,
  getOperators,
  testExpression,
  ExpressionTestResult,
} from '../services/expressionService';
import { getAllCustomFields } from '../services/configurationService';
import { mergeCustomFieldsWithMetadata } from '../utils/custom-field-helper';
import { FieldMetadata, OperatorInfo, MetadataContextType } from '../models/expression';

/** Field metadata for an expression context, merged with the tenant's custom fields. */
export const useExpressionFields = ({ contextType }: { contextType: MetadataContextType }) => {
  return useQuery<FieldMetadata[]>({
    queryKey: ['expression-fields', contextType],
    queryFn: async () => {
      const [fields, customFields] = await Promise.all([getFieldMetadata(contextType), getAllCustomFields()]);
      return mergeCustomFieldsWithMetadata(fields, customFields);
    },
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
};

/** Comparison operators usable in the expression builder (logical And/Or/Not are excluded). */
export const useExpressionOperators = () => {
  return useQuery<OperatorInfo[], Error, OperatorInfo[]>({
    queryKey: ['expression-operators'],
    queryFn: getOperators,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    select: (operators) => operators.filter((operator) => operator.isLogical && !['Not', 'And', 'Or'].includes(operator.operator)),
  });
};

/** Parses an expression string into its structured form; idle when there is nothing to parse. */
export const useParsedExpression = ({ expression }: { expression: string | null | undefined }) => {
  return useQuery<ExpressionTestResult>({
    queryKey: ['expression-parse', expression],
    queryFn: () => testExpression(expression as string),
    enabled: !!expression?.trim(),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
};
